import { useEffect, useRef, useState } from "react";
import { api } from "@/lib/api";
import { toast } from "sonner";
import { emit as emitEvent } from "@/lib/events";
import { Award, Plus, Loader2, Search, Trash2, Pencil, X, Save, AlertCircle, Star, Target, Upload, FileSpreadsheet, ClipboardPaste, Sparkles, FileText, CheckSquare, Square } from "lucide-react";

const money = (v) =>
  v == null || v === "" || isNaN(Number(v))
    ? "—"
    : `$${Number(v).toLocaleString(undefined, { maximumFractionDigits: 0 })}`;

const EMPTY = {
  project_title: "",
  customer: "",
  contract_number: "",
  contract_value: "",
  period_start: "",
  period_end: "",
  scope: "",
  naics_codes: "",
  keywords: "",
  poc_name: "",
  poc_email: "",
  poc_phone: "",
  outcome: "",
  cpars_rating: "",
};

function toCsv(v) {
  if (Array.isArray(v)) return v.join(", ");
  return v || "";
}

function fromCsv(s) {
  return (s || "")
    .split(",")
    .map((x) => x.trim())
    .filter(Boolean);
}

export default function PastPerformance() {
  const [items, setItems] = useState([]);
  const [q, setQ] = useState("");
  const [loading, setLoading] = useState(true);
  const [editing, setEditing] = useState(null); // pp_id or "new"
  const [form, setForm] = useState(EMPTY);
  const [saving, setSaving] = useState(false);
  const [bulkOpen, setBulkOpen] = useState(false);
  const [bulkTab, setBulkTab] = useState("paste"); // "paste" | "file"
  const [pasteText, setPasteText] = useState("");
  const [bulkBusy, setBulkBusy] = useState(false);
  const [dragging, setDragging] = useState(false);
  const [lastImport, setLastImport] = useState(null);
  const bulkFileRef = useRef(null);

  // AI extraction from a capabilities statement
  const [extractOpen, setExtractOpen] = useState(false);
  const [extractBusy, setExtractBusy] = useState(false);
  const [extractResult, setExtractResult] = useState(null); // { source, items }
  const [extractSelected, setExtractSelected] = useState({}); // { idx: bool }
  const [extractSaving, setExtractSaving] = useState(false);
  const [extractTab, setExtractTab] = useState("upload"); // "upload" | "library"
  const [libraryDocs, setLibraryDocs] = useState([]);
  const [libraryLoading, setLibraryLoading] = useState(false);
  const [libraryFilter, setLibraryFilter] = useState("");
  const extractFileRef = useRef(null);

  const load = async () => {
    setLoading(true);
    try {
      const r = await api.get("/past-performance", { params: q ? { q } : {} });
      setItems(r.data.items || []);
    } catch (err) {
      console.error("bulk import list failed:", err);
      /* keep list state as-is — the search debounce will retry */
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    // eslint-disable-next-line
  }, []);

  useEffect(() => {
    const t = setTimeout(() => load(), 300);
    return () => clearTimeout(t);
    // eslint-disable-next-line
  }, [q]);

  const startNew = () => {
    setForm(EMPTY);
    setEditing("new");
  };

  const startEdit = (pp) => {
    setForm({
      ...EMPTY,
      ...pp,
      contract_value: pp.contract_value ?? "",
      naics_codes: toCsv(pp.naics_codes),
      keywords: toCsv(pp.keywords),
    });
    setEditing(pp.pp_id);
  };

  const cancel = () => {
    setEditing(null);
    setForm(EMPTY);
  };

  const save = async () => {
    if (!form.project_title.trim()) {
      toast.error("Project title is required");
      return;
    }
    setSaving(true);
    const payload = {
      project_title: form.project_title.trim(),
      customer: form.customer || null,
      contract_number: form.contract_number || null,
      contract_value:
        form.contract_value === "" || form.contract_value == null
          ? null
          : Number(form.contract_value),
      period_start: form.period_start || null,
      period_end: form.period_end || null,
      scope: form.scope || null,
      naics_codes: fromCsv(form.naics_codes),
      keywords: fromCsv(form.keywords),
      poc_name: form.poc_name || null,
      poc_email: form.poc_email || null,
      poc_phone: form.poc_phone || null,
      outcome: form.outcome || null,
      cpars_rating: form.cpars_rating || null,
    };
    try {
      if (editing === "new") {
        await api.post("/past-performance", payload);
        toast.success("Past performance added");
      } else {
        await api.put(`/past-performance/${editing}`, payload);
        toast.success("Past performance updated");
      }
      setEditing(null);
      setForm(EMPTY);
      await load();
      emitEvent("past_performance:changed", { reason: editing === "new" ? "add" : "update" });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Could not save");
    } finally {
      setSaving(false);
    }
  };

  const remove = async (pp) => {
    if (!window.confirm(`Delete "${pp.project_title}"?`)) return;
    try {
      await api.delete(`/past-performance/${pp.pp_id}`);
      setItems((its) => its.filter((r) => r.pp_id !== pp.pp_id));
      toast.success("Removed");
      emitEvent("past_performance:changed", { reason: "delete", pp_id: pp.pp_id });
    } catch (err) {
      console.error("delete past-performance failed:", err);
      toast.error("Could not remove");
    }
  };

  const runBulkImport = async ({ file, text } = {}) => {
    setBulkBusy(true);
    setLastImport(null);
    try {
      const fd = new FormData();
      if (file) fd.append("file", file);
      else if (text && text.trim()) fd.append("text", text);
      else {
        toast.error("Paste rows or choose a file first");
        setBulkBusy(false);
        return;
      }
      const r = await api.post("/past-performance/bulk-import", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      const { imported, skipped, column_mapping, source } = r.data;
      setLastImport({ imported, skipped, column_mapping, source });
      toast.success(
        `Imported ${imported} record${imported === 1 ? "" : "s"}` +
          (skipped > 0 ? ` · ${skipped} skipped` : "")
      );
      setBulkOpen(false);
      setPasteText("");
      if (bulkFileRef.current) bulkFileRef.current.value = "";
      await load();
      emitEvent("past_performance:changed", { reason: "bulk_import", imported });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Bulk import failed");
    } finally {
      setBulkBusy(false);
    }
  };

  const onBulkFile = (f) => {
    if (!f) return;
    const okExt = /\.(csv|tsv|xlsx|xls)$/i.test(f.name);
    if (!okExt) {
      toast.error("Only .csv, .tsv, .xls, and .xlsx are supported");
      return;
    }
    runBulkImport({ file: f });
  };

  // ── AI extraction from a capabilities statement ────────────────────────
  const runExtract = async (source) => {
    // `source` is either a File OR { doc_id, name }
    if (!source) return;
    const isFile = source instanceof File;
    if (isFile && !/\.(pdf|docx|txt)$/i.test(source.name)) {
      toast.error("Only .pdf, .docx, .txt are supported"); return;
    }
    setExtractBusy(true);
    setExtractResult(null);
    setExtractSelected({});
    try {
      const fd = new FormData();
      if (isFile) fd.append("file", source);
      else fd.append("doc_id", source.doc_id);
      const r = await api.post("/past-performance/extract-from-capabilities", fd, {
        headers: { "Content-Type": "multipart/form-data" },
      });
      const items = r.data.items || [];
      setExtractResult({ source: r.data.source, items, chars: r.data.chars_analyzed });
      const sel = {};
      items.forEach((_, i) => { sel[i] = true; });
      setExtractSelected(sel);
      if (items.length === 0) {
        toast("No past-performance projects detected in that document", { icon: "🔍" });
      } else {
        toast.success(`Extracted ${items.length} project${items.length === 1 ? "" : "s"} — review and save`);
      }
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Extraction failed");
    } finally { setExtractBusy(false); }
  };

  const loadLibrary = async () => {
    setLibraryLoading(true);
    try {
      const r = await api.get("/documents");
      // Backend returns an array; filter to text-bearing docs and skip empties.
      const rows = Array.isArray(r.data) ? r.data : [];
      const filtered = rows.filter((d) => {
        if (d?.is_deleted) return false;
        const ext = (d?.name || "").split(".").pop()?.toLowerCase();
        return ["pdf", "docx", "txt"].includes(ext) && (d?.content || "").trim().length >= 200;
      });
      setLibraryDocs(filtered);
    } catch (e) {
      toast.error("Couldn't load documents");
    } finally { setLibraryLoading(false); }
  };

  // Lazy-load the library the first time the tab is opened.
  useEffect(() => {
    if (extractOpen && extractTab === "library" && libraryDocs.length === 0 && !libraryLoading) {
      loadLibrary();
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [extractOpen, extractTab]);

  const saveExtracted = async () => {
    if (!extractResult) return;
    const items = extractResult.items.filter((_, i) => extractSelected[i]);
    if (items.length === 0) { toast.error("Select at least one project to save"); return; }
    setExtractSaving(true);
    try {
      const r = await api.post("/past-performance/bulk-save", {
        items,
        source: extractResult.source,
      });
      toast.success(`Saved ${r.data.saved} record${r.data.saved === 1 ? "" : "s"}`);
      setExtractOpen(false);
      setExtractResult(null);
      setExtractSelected({});
      if (extractFileRef.current) extractFileRef.current.value = "";
      await load();
      emitEvent("past_performance:changed", { reason: "capabilities_extract", saved: r.data.saved });
    } catch (e) {
      toast.error(e?.response?.data?.detail || "Save failed");
    } finally { setExtractSaving(false); }
  };

  const extractSelectedCount = extractResult
    ? Object.values(extractSelected).filter(Boolean).length
    : 0;

  const isEditing = editing !== null;

  return (
    <div className="p-6 md:p-10 max-w-6xl mx-auto">
      <div className="flex items-center gap-4 mb-2">
        <div className="w-12 h-12 rounded-2xl bg-gradient-to-br from-amber-400 to-orange-500 flex items-center justify-center shadow-lg shadow-orange-500/30">
          <Award className="w-6 h-6 text-white" />
        </div>
        <div>
          <div className="text-[10px] font-mono font-bold tracking-widest text-orange-600">
            // PAST PERFORMANCE
          </div>
          <h1 className="font-display font-black text-3xl md:text-4xl text-slate-900">
            Feed the AI your best references
          </h1>
        </div>
      </div>
      <p className="text-slate-600 max-w-3xl mb-8">
        Add every completed contract that could make your next bid stronger. When you generate a proposal, BidForge
        automatically ranks these by NAICS overlap, keyword hits, and scope similarity — the top matches are cited by
        name inside the proposal so evaluators see relevant experience first.
      </p>

      {/* Top bar: search + add */}
      <div className="flex items-center gap-3 mb-6 flex-wrap">
        <div className="relative flex-1 min-w-[220px]">
          <Search className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
          <input
            value={q}
            onChange={(e) => setQ(e.target.value)}
            placeholder="Search title, scope, or keyword"
            className="w-full bg-white border border-slate-200 rounded-full pl-10 pr-4 py-2.5 text-sm focus:border-orange-400 outline-none"
            data-testid="pp-search"
          />
        </div>
        {!isEditing && (
          <>
            <button
              onClick={() => setExtractOpen(true)}
              className="text-xs font-mono font-bold tracking-widest text-fuchsia-700 hover:text-fuchsia-900 border border-fuchsia-300 hover:border-fuchsia-500 bg-white rounded-full px-4 py-2.5 inline-flex items-center gap-2"
              data-testid="pp-extract-btn"
            >
              <Sparkles className="w-3.5 h-3.5" />
              EXTRACT FROM CAPABILITIES
            </button>
            <button
              onClick={() => setBulkOpen(true)}
              className="text-xs font-mono font-bold tracking-widest text-orange-700 hover:text-orange-900 border border-orange-300 hover:border-orange-500 bg-white rounded-full px-4 py-2.5 inline-flex items-center gap-2"
              data-testid="pp-bulk-btn"
            >
              <Upload className="w-3.5 h-3.5" />
              BULK IMPORT
            </button>
            <button
              onClick={startNew}
              className="bf-pill text-xs px-6 py-2.5 inline-flex items-center gap-2"
              data-testid="pp-add-btn"
            >
              <Plus className="w-3.5 h-3.5" />
              ADD PAST PERFORMANCE
            </button>
          </>
        )}
      </div>

      {/* Last bulk-import summary */}
      {lastImport && (
        <div
          className="rounded-xl border border-emerald-200 bg-emerald-50 p-4 mb-6 flex items-start gap-3"
          data-testid="pp-last-import-card"
        >
          <FileSpreadsheet className="w-5 h-5 text-emerald-600 mt-0.5 shrink-0" />
          <div className="flex-1">
            <div className="font-display font-bold text-emerald-900">
              Imported {lastImport.imported} record
              {lastImport.imported === 1 ? "" : "s"} from {lastImport.source}
              {lastImport.skipped > 0 && (
                <span className="text-emerald-700 font-normal"> · {lastImport.skipped} skipped</span>
              )}
            </div>
            <div className="text-xs text-emerald-700 mt-1 flex flex-wrap gap-x-3 gap-y-1">
              {Object.entries(lastImport.column_mapping || {})
                .filter(([, v]) => v)
                .map(([k, v]) => (
                  <span key={k} className="font-mono">
                    {k}: <span className="font-bold">{v}</span>
                  </span>
                ))}
            </div>
          </div>
          <button
            onClick={() => setLastImport(null)}
            className="text-emerald-600 hover:text-emerald-800"
            data-testid="pp-last-import-close"
          >
            <X className="w-4 h-4" />
          </button>
        </div>
      )}

      {/* Bulk import modal */}
      {bulkOpen && (
        <div
          className="fixed inset-0 z-40 bg-slate-900/40 backdrop-blur-sm flex items-center justify-center p-4"
          onClick={() => !bulkBusy && setBulkOpen(false)}
          data-testid="pp-bulk-modal"
        >
          <div
            className="bg-white rounded-2xl shadow-2xl border border-slate-200 max-w-3xl w-full overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between px-6 py-4 border-b border-slate-100">
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 rounded-xl bg-orange-100 text-orange-600 flex items-center justify-center">
                  <Upload className="w-4 h-4" />
                </div>
                <div>
                  <div className="font-display font-black text-xl text-slate-900">Bulk import past performance</div>
                  <div className="text-xs text-slate-500">
                    Seed 20+ references in one shot. CSV, TSV, XLSX, or paste from a spreadsheet.
                  </div>
                </div>
              </div>
              <button
                onClick={() => !bulkBusy && setBulkOpen(false)}
                className="text-slate-400 hover:text-slate-700 disabled:opacity-40"
                disabled={bulkBusy}
                data-testid="pp-bulk-close"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            {/* Tabs */}
            <div className="flex gap-1 border-b border-slate-100 px-4 pt-3 bg-slate-50/60">
              <button
                onClick={() => setBulkTab("paste")}
                className={`px-4 py-2 text-xs font-mono font-bold tracking-widest rounded-t-lg border-b-2 -mb-px ${
                  bulkTab === "paste"
                    ? "border-orange-500 text-orange-700 bg-white"
                    : "border-transparent text-slate-500 hover:text-slate-800"
                }`}
                data-testid="pp-bulk-tab-paste"
              >
                <ClipboardPaste className="w-3.5 h-3.5 inline mr-1.5" />
                PASTE FROM SPREADSHEET
              </button>
              <button
                onClick={() => setBulkTab("file")}
                className={`px-4 py-2 text-xs font-mono font-bold tracking-widest rounded-t-lg border-b-2 -mb-px ${
                  bulkTab === "file"
                    ? "border-orange-500 text-orange-700 bg-white"
                    : "border-transparent text-slate-500 hover:text-slate-800"
                }`}
                data-testid="pp-bulk-tab-file"
              >
                <FileSpreadsheet className="w-3.5 h-3.5 inline mr-1.5" />
                UPLOAD FILE
              </button>
            </div>

            <div className="p-6">
              {bulkTab === "paste" ? (
                <>
                  <div className="text-xs text-slate-600 mb-3">
                    Copy rows straight from Excel or Google Sheets (tabs) or paste CSV. First row must be headers.
                    Recognized columns:{" "}
                    <code className="font-mono text-slate-800">Project Title</code>,{" "}
                    <code className="font-mono text-slate-800">Customer</code>,{" "}
                    <code className="font-mono text-slate-800">Contract Value</code>,{" "}
                    <code className="font-mono text-slate-800">Period Start</code>,{" "}
                    <code className="font-mono text-slate-800">Period End</code>,{" "}
                    <code className="font-mono text-slate-800">Scope</code>,{" "}
                    <code className="font-mono text-slate-800">NAICS</code>,{" "}
                    <code className="font-mono text-slate-800">Keywords</code>,{" "}
                    <code className="font-mono text-slate-800">POC Email</code>,{" "}
                    <code className="font-mono text-slate-800">CPARS</code>.
                  </div>
                  <textarea
                    value={pasteText}
                    onChange={(e) => setPasteText(e.target.value)}
                    rows={10}
                    placeholder={
                      "Project Title,Customer,Contract Value,Scope,NAICS,Keywords,CPARS\n" +
                      "Camp Pendleton BOQ Reroof,Dept of Navy,425000,Standing seam metal roofing 42000 sf,238160;236220,\"metal roofing,standing seam\",Exceptional\n" +
                      "Fort Sill HVAC Retrofit,Dept of Army,285000,Replaced 12 rooftop HVAC units,238220,\"hvac,ductwork\",Very Good"
                    }
                    className="w-full font-mono text-xs bg-slate-50 border border-slate-200 rounded-lg px-3 py-2 focus:border-orange-400 outline-none"
                    data-testid="pp-bulk-paste-textarea"
                  />
                  <div className="flex items-center justify-between mt-4">
                    <button
                      onClick={() =>
                        setPasteText(
                          "Project Title,Customer,Contract Value,Period Start,Period End,Scope,NAICS,Keywords,POC Email,CPARS\n" +
                            "Sample Roofing Project,Dept of Navy,425000,2023-08,2024-02,42000 sf standing seam metal roof,238160,\"metal roofing,standing seam\",poc@navy.mil,Exceptional\n"
                        )
                      }
                      className="text-xs font-mono font-bold tracking-widest text-slate-500 hover:text-slate-800"
                      data-testid="pp-bulk-sample"
                    >
                      LOAD SAMPLE
                    </button>
                    <button
                      onClick={() => runBulkImport({ text: pasteText })}
                      disabled={bulkBusy || !pasteText.trim()}
                      className="bf-pill text-xs px-6 py-2.5 inline-flex items-center gap-2 disabled:opacity-50"
                      data-testid="pp-bulk-paste-import"
                    >
                      {bulkBusy ? (
                        <Loader2 className="w-3.5 h-3.5 animate-spin" />
                      ) : (
                        <Upload className="w-3.5 h-3.5" />
                      )}
                      {bulkBusy ? "IMPORTING" : "IMPORT ROWS"}
                    </button>
                  </div>
                </>
              ) : (
                <div
                  onDragOver={(e) => {
                    e.preventDefault();
                    setDragging(true);
                  }}
                  onDragLeave={() => setDragging(false)}
                  onDrop={(e) => {
                    e.preventDefault();
                    setDragging(false);
                    const f = e.dataTransfer?.files?.[0];
                    if (f) onBulkFile(f);
                  }}
                  className={`rounded-2xl border-2 border-dashed p-10 text-center transition-all ${
                    dragging ? "border-orange-500 bg-orange-50" : "border-slate-300 bg-slate-50/50 hover:border-orange-400"
                  }`}
                  data-testid="pp-bulk-dropzone"
                >
                  <Upload
                    className={`w-10 h-10 mx-auto mb-3 ${dragging ? "text-orange-600" : "text-slate-400"}`}
                  />
                  <div className="font-display font-bold text-lg text-slate-900 mb-1">
                    {bulkBusy ? "Importing…" : "Drop a spreadsheet here"}
                  </div>
                  <div className="text-sm text-slate-500 mb-4">CSV, TSV, XLSX, or XLS · up to 5 MB · 500 rows</div>
                  <input
                    ref={bulkFileRef}
                    type="file"
                    accept=".csv,.tsv,.xlsx,.xls"
                    className="hidden"
                    onChange={(e) => onBulkFile(e.target.files?.[0])}
                    data-testid="pp-bulk-file-input"
                  />
                  <button
                    onClick={() => bulkFileRef.current?.click()}
                    disabled={bulkBusy}
                    className="bf-pill text-xs px-6 py-2.5 inline-flex items-center gap-2 disabled:opacity-50"
                    data-testid="pp-bulk-file-choose"
                  >
                    {bulkBusy ? (
                      <Loader2 className="w-3.5 h-3.5 animate-spin" />
                    ) : (
                      <Upload className="w-3.5 h-3.5" />
                    )}
                    {bulkBusy ? "IMPORTING" : "CHOOSE FILE"}
                  </button>
                  <div className="text-xs text-slate-400 mt-4">
                    Column headers are fuzzy-matched — you can label them "Project", "Title", "Job Name" etc.
                  </div>
                </div>
              )}
            </div>
          </div>
        </div>
      )}

      {/* AI extraction modal — capabilities statement → structured PP list */}
      {extractOpen && (
        <div
          className="fixed inset-0 z-40 bg-slate-900/40 backdrop-blur-sm flex items-center justify-center p-4"
          onClick={() => !extractBusy && !extractSaving && setExtractOpen(false)}
          data-testid="pp-extract-modal"
        >
          <div
            className="bg-white rounded-2xl shadow-2xl border border-slate-200 max-w-4xl w-full max-h-[90vh] flex flex-col overflow-hidden"
            onClick={(e) => e.stopPropagation()}
          >
            <div className="flex items-center justify-between px-6 py-4 border-b border-slate-100">
              <div className="flex items-center gap-3">
                <div className="w-9 h-9 rounded-xl bg-fuchsia-100 text-fuchsia-600 flex items-center justify-center">
                  <Sparkles className="w-4 h-4" />
                </div>
                <div>
                  <div className="font-display font-black text-xl text-slate-900">Extract from capabilities statement</div>
                  <div className="text-xs text-slate-500">
                    Upload a capabilities statement, SOQ, or marketing packet — Claude pulls out every past-performance project so you can review and save.
                  </div>
                </div>
              </div>
              <button
                onClick={() => !extractBusy && !extractSaving && setExtractOpen(false)}
                className="text-slate-400 hover:text-slate-700 disabled:opacity-40"
                disabled={extractBusy || extractSaving}
                data-testid="pp-extract-close"
              >
                <X className="w-5 h-5" />
              </button>
            </div>

            <div className="p-6 overflow-y-auto flex-1">
              {!extractResult ? (
                <>
                  {/* Source picker tabs */}
                  {!extractBusy && (
                    <div className="flex gap-1 mb-4 border-b border-slate-100">
                      <button
                        onClick={() => setExtractTab("upload")}
                        className={`px-4 py-2 text-xs font-mono font-bold tracking-widest border-b-2 -mb-px ${
                          extractTab === "upload"
                            ? "border-fuchsia-500 text-fuchsia-700"
                            : "border-transparent text-slate-500 hover:text-slate-800"
                        }`}
                        data-testid="pp-extract-tab-upload"
                      >
                        <Upload className="w-3.5 h-3.5 inline mr-1.5" />
                        UPLOAD FILE
                      </button>
                      <button
                        onClick={() => setExtractTab("library")}
                        className={`px-4 py-2 text-xs font-mono font-bold tracking-widest border-b-2 -mb-px ${
                          extractTab === "library"
                            ? "border-fuchsia-500 text-fuchsia-700"
                            : "border-transparent text-slate-500 hover:text-slate-800"
                        }`}
                        data-testid="pp-extract-tab-library"
                      >
                        <FileText className="w-3.5 h-3.5 inline mr-1.5" />
                        FROM DOCUMENTS LIBRARY
                      </button>
                    </div>
                  )}

                  {extractBusy ? (
                    <div className="rounded-2xl border-2 border-dashed border-slate-300 bg-slate-50/50 p-10 text-center">
                      <Loader2 className="w-10 h-10 mx-auto mb-3 text-fuchsia-500 animate-spin" />
                      <div className="font-display font-bold text-lg text-slate-900 mb-1">Reading your capabilities statement…</div>
                      <div className="text-sm text-slate-500">Claude is pulling out every past-performance project. Usually takes 5–15 seconds.</div>
                    </div>
                  ) : extractTab === "upload" ? (
                    <div
                      onDragOver={(e) => e.preventDefault()}
                      onDrop={(e) => {
                        e.preventDefault();
                        const f = e.dataTransfer?.files?.[0];
                        if (f) runExtract(f);
                      }}
                      className="rounded-2xl border-2 border-dashed border-slate-300 bg-slate-50/50 hover:border-fuchsia-400 p-10 text-center transition-all"
                      data-testid="pp-extract-dropzone"
                    >
                      <FileText className="w-10 h-10 mx-auto mb-3 text-slate-400" />
                      <div className="font-display font-bold text-lg text-slate-900 mb-1">Drop a capabilities statement here</div>
                      <div className="text-sm text-slate-500 mb-4">PDF · DOCX · TXT · up to 15 MB · costs 1 credit</div>
                      <input
                        ref={extractFileRef}
                        type="file"
                        accept=".pdf,.docx,.txt"
                        className="hidden"
                        onChange={(e) => runExtract(e.target.files?.[0])}
                        data-testid="pp-extract-file-input"
                      />
                      <button
                        onClick={() => extractFileRef.current?.click()}
                        className="bf-pill text-xs px-6 py-2.5 inline-flex items-center gap-2"
                        data-testid="pp-extract-choose"
                      >
                        <Upload className="w-3.5 h-3.5" />
                        CHOOSE FILE
                      </button>
                      <div className="text-xs text-slate-400 mt-4 max-w-md mx-auto">
                        Extraction is grounded — Claude will not invent projects or POCs that aren't in the document.
                        Nothing is saved until you review and confirm.
                      </div>
                    </div>
                  ) : (
                    <div data-testid="pp-extract-library">
                      <div className="flex items-center gap-3 mb-3">
                        <div className="relative flex-1">
                          <Search className="w-4 h-4 text-slate-400 absolute left-3 top-1/2 -translate-y-1/2" />
                          <input
                            value={libraryFilter}
                            onChange={(e) => setLibraryFilter(e.target.value)}
                            placeholder="Filter by name or type"
                            className="w-full bg-white border border-slate-200 rounded-full pl-10 pr-4 py-2 text-sm focus:border-fuchsia-400 outline-none"
                            data-testid="pp-extract-library-filter"
                          />
                        </div>
                        <button
                          onClick={loadLibrary}
                          disabled={libraryLoading}
                          className="text-xs font-mono font-bold tracking-widest text-slate-500 hover:text-slate-800 disabled:opacity-40"
                          data-testid="pp-extract-library-refresh"
                        >
                          REFRESH
                        </button>
                      </div>

                      {libraryLoading ? (
                        <div className="text-center py-10">
                          <Loader2 className="w-6 h-6 mx-auto text-slate-400 animate-spin mb-2" />
                          <div className="text-sm text-slate-500">Loading your documents…</div>
                        </div>
                      ) : libraryDocs.length === 0 ? (
                        <div className="rounded-xl border border-slate-200 bg-slate-50 p-8 text-center">
                          <FileText className="w-8 h-8 text-slate-400 mx-auto mb-2" />
                          <div className="font-display font-bold text-slate-800">No usable documents in your library yet</div>
                          <div className="text-sm text-slate-500 mt-1 max-w-md mx-auto">
                            Upload a PDF, DOCX, or TXT on the <span className="font-mono">Documents</span> page — anything text-bearing and at least 200 characters shows up here.
                          </div>
                        </div>
                      ) : (() => {
                          const needle = libraryFilter.trim().toLowerCase();
                          const filtered = needle
                            ? libraryDocs.filter((d) =>
                                (d.name || "").toLowerCase().includes(needle) ||
                                (d.doc_type || "").toLowerCase().includes(needle),
                              )
                            : libraryDocs;
                          if (filtered.length === 0) {
                            return (
                              <div className="rounded-xl border border-slate-200 bg-slate-50 p-6 text-center text-sm text-slate-500">
                                No documents match "{libraryFilter}".
                              </div>
                            );
                          }
                          return (
                            <div className="space-y-2 max-h-[52vh] overflow-y-auto pr-1">
                              {filtered.map((d) => {
                                const ext = (d.name || "").split(".").pop()?.toUpperCase() || "DOC";
                                const size = d.content ? d.content.length : 0;
                                return (
                                  <button
                                    key={d.doc_id}
                                    onClick={() => runExtract({ doc_id: d.doc_id, name: d.name })}
                                    className="w-full text-left rounded-xl border border-slate-200 hover:border-fuchsia-400 hover:bg-fuchsia-50/40 bg-white p-3 flex items-center gap-3 transition-all"
                                    data-testid={`pp-extract-library-doc-${d.doc_id}`}
                                  >
                                    <div className="w-9 h-9 rounded-lg bg-slate-100 text-slate-500 flex items-center justify-center font-mono text-[10px] font-bold flex-shrink-0">
                                      {ext}
                                    </div>
                                    <div className="flex-1 min-w-0">
                                      <div className="font-display font-bold text-slate-900 truncate">{d.name}</div>
                                      <div className="text-xs text-slate-500 mt-0.5">
                                        {d.doc_type && <span className="font-mono">{d.doc_type}</span>}
                                        {d.doc_type && <span className="text-slate-300"> · </span>}
                                        <span>{size.toLocaleString()} chars</span>
                                        {d.created_at && (
                                          <>
                                            <span className="text-slate-300"> · </span>
                                            <span>{new Date(d.created_at).toLocaleDateString()}</span>
                                          </>
                                        )}
                                      </div>
                                    </div>
                                    <Sparkles className="w-4 h-4 text-fuchsia-500 flex-shrink-0" />
                                  </button>
                                );
                              })}
                            </div>
                          );
                        })()}
                    </div>
                  )}
                </>
              ) : (
                <div data-testid="pp-extract-preview">
                  <div className="flex items-center justify-between mb-4 flex-wrap gap-2">
                    <div>
                      <div className="font-display font-bold text-slate-900">
                        Found {extractResult.items.length} project{extractResult.items.length === 1 ? "" : "s"} in {extractResult.source}
                      </div>
                      <div className="text-xs text-slate-500">Uncheck anything that looks wrong before saving. {extractSelectedCount} of {extractResult.items.length} selected.</div>
                    </div>
                    <div className="flex gap-2">
                      <button
                        onClick={() => {
                          const all = {};
                          extractResult.items.forEach((_, i) => { all[i] = true; });
                          setExtractSelected(all);
                        }}
                        className="text-xs font-mono font-bold tracking-widest text-slate-500 hover:text-slate-800"
                        data-testid="pp-extract-select-all"
                      >
                        SELECT ALL
                      </button>
                      <button
                        onClick={() => setExtractSelected({})}
                        className="text-xs font-mono font-bold tracking-widest text-slate-500 hover:text-slate-800"
                        data-testid="pp-extract-select-none"
                      >
                        NONE
                      </button>
                      <button
                        onClick={() => { setExtractResult(null); setExtractSelected({}); if (extractFileRef.current) extractFileRef.current.value = ""; }}
                        className="text-xs font-mono font-bold tracking-widest text-slate-500 hover:text-slate-800"
                        data-testid="pp-extract-restart"
                      >
                        UPLOAD ANOTHER
                      </button>
                    </div>
                  </div>

                  {extractResult.items.length === 0 ? (
                    <div className="rounded-xl border border-amber-200 bg-amber-50 p-6 text-center">
                      <AlertCircle className="w-8 h-8 text-amber-600 mx-auto mb-2" />
                      <div className="font-display font-bold text-amber-900">No past-performance projects detected</div>
                      <div className="text-sm text-amber-700 mt-1">
                        The document may be a services overview or company bio without concrete project cites.
                        Try uploading a Statement of Qualifications (SOQ) or a project experience packet.
                      </div>
                    </div>
                  ) : (
                    <div className="space-y-3">
                      {extractResult.items.map((it, i) => (
                        <div
                          key={i}
                          onClick={() => setExtractSelected((s) => ({ ...s, [i]: !s[i] }))}
                          className={`rounded-xl border cursor-pointer p-4 transition-all ${
                            extractSelected[i]
                              ? "border-fuchsia-400 bg-fuchsia-50/50"
                              : "border-slate-200 bg-white hover:border-slate-300 opacity-60"
                          }`}
                          data-testid={`pp-extract-row-${i}`}
                        >
                          <div className="flex items-start gap-3">
                            <div className="mt-0.5 flex-shrink-0 text-fuchsia-600">
                              {extractSelected[i] ? <CheckSquare className="w-5 h-5" /> : <Square className="w-5 h-5 text-slate-400" />}
                            </div>
                            <div className="flex-1 min-w-0">
                              <div className="flex items-center justify-between gap-4 flex-wrap">
                                <div className="font-display font-bold text-slate-900">{it.project_title}</div>
                                <div className="text-sm font-mono font-bold text-slate-700">{money(it.contract_value)}</div>
                              </div>
                              <div className="text-xs text-slate-600 mt-0.5">
                                {it.customer || "—"}
                                {it.contract_number && <span className="text-slate-400"> · {it.contract_number}</span>}
                                {(it.period_start || it.period_end) && (
                                  <span className="text-slate-400"> · {[it.period_start, it.period_end].filter(Boolean).join(" – ")}</span>
                                )}
                              </div>
                              {it.scope && <div className="text-sm text-slate-700 mt-2 leading-relaxed">{it.scope}</div>}
                              <div className="flex flex-wrap gap-1.5 mt-2 text-[10px] font-mono">
                                {(it.keywords || []).map((k, ki) => (
                                  <span key={ki} className="text-fuchsia-700 bg-fuchsia-100 rounded-full px-2 py-0.5">{k}</span>
                                ))}
                                {it.cpars_rating && (
                                  <span className="text-emerald-700 bg-emerald-100 rounded-full px-2 py-0.5">CPARS: {it.cpars_rating}</span>
                                )}
                                {it.poc_name && (
                                  <span className="text-slate-600 bg-slate-100 rounded-full px-2 py-0.5">POC: {it.poc_name}</span>
                                )}
                              </div>
                            </div>
                          </div>
                        </div>
                      ))}
                    </div>
                  )}
                </div>
              )}
            </div>

            {extractResult && extractResult.items.length > 0 && (
              <div className="border-t border-slate-100 px-6 py-4 flex items-center justify-between bg-slate-50/60">
                <div className="text-xs text-slate-500">
                  Grounded extraction — Claude won't invent projects or POCs that aren't in the source.
                </div>
                <button
                  onClick={saveExtracted}
                  disabled={extractSaving || extractSelectedCount === 0}
                  className="bf-pill text-xs px-6 py-2.5 inline-flex items-center gap-2 disabled:opacity-50"
                  data-testid="pp-extract-save"
                >
                  {extractSaving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />}
                  {extractSaving ? "SAVING…" : `SAVE ${extractSelectedCount} TO LIBRARY`}
                </button>
              </div>
            )}
          </div>
        </div>
      )}

      {/* Editor */}
      {isEditing && (
        <div
          className="rounded-2xl border border-orange-200 bg-orange-50/50 p-6 mb-6"
          data-testid="pp-editor"
        >
          <div className="flex items-center justify-between mb-4">
            <div className="font-display font-black text-xl text-slate-900">
              {editing === "new" ? "New past performance" : "Edit past performance"}
            </div>
            <button
              onClick={cancel}
              className="text-slate-400 hover:text-slate-700"
              data-testid="pp-cancel-btn"
            >
              <X className="w-5 h-5" />
            </button>
          </div>

          <div className="grid md:grid-cols-2 gap-4">
            <Field label="Project title *" testid="pp-project-title">
              <input
                value={form.project_title}
                onChange={(e) => setForm({ ...form, project_title: e.target.value })}
                className={inputCls}
                data-testid="pp-project-title-input"
                placeholder="Middle Residence Reroof — Camp Pendleton"
              />
            </Field>
            <Field label="Customer / agency" testid="pp-customer">
              <input
                value={form.customer}
                onChange={(e) => setForm({ ...form, customer: e.target.value })}
                className={inputCls}
                data-testid="pp-customer-input"
                placeholder="Department of the Navy"
              />
            </Field>
            <Field label="Contract number">
              <input
                value={form.contract_number}
                onChange={(e) => setForm({ ...form, contract_number: e.target.value })}
                className={inputCls}
                placeholder="N62473-23-C-4501"
              />
            </Field>
            <Field label="Contract value (USD)">
              <input
                type="number"
                value={form.contract_value}
                onChange={(e) => setForm({ ...form, contract_value: e.target.value })}
                className={inputCls}
                placeholder="425000"
              />
            </Field>
            <Field label="Period start">
              <input
                value={form.period_start}
                onChange={(e) => setForm({ ...form, period_start: e.target.value })}
                className={inputCls}
                placeholder="2023-08 or Aug 2023"
              />
            </Field>
            <Field label="Period end">
              <input
                value={form.period_end}
                onChange={(e) => setForm({ ...form, period_end: e.target.value })}
                className={inputCls}
                placeholder="2024-02 or ongoing"
              />
            </Field>
            <Field label="NAICS codes (comma-separated)">
              <input
                value={form.naics_codes}
                onChange={(e) => setForm({ ...form, naics_codes: e.target.value })}
                className={inputCls}
                placeholder="238160, 236220"
              />
            </Field>
            <Field label="Keywords / tags (comma-separated)">
              <input
                value={form.keywords}
                onChange={(e) => setForm({ ...form, keywords: e.target.value })}
                className={inputCls}
                placeholder="metal roofing, standing seam, TPO"
              />
            </Field>
            <div className="md:col-span-2">
              <Field label="Scope of work">
                <textarea
                  value={form.scope}
                  onChange={(e) => setForm({ ...form, scope: e.target.value })}
                  rows={4}
                  className={inputCls}
                  data-testid="pp-scope-input"
                  placeholder="Removed and replaced 42,000 sf of standing seam metal roofing on two barracks, including new underlayment, flashing, and gutter system."
                />
              </Field>
            </div>
            <Field label="POC name">
              <input
                value={form.poc_name}
                onChange={(e) => setForm({ ...form, poc_name: e.target.value })}
                className={inputCls}
                placeholder="John Smith, ROICC"
              />
            </Field>
            <Field label="POC email">
              <input
                value={form.poc_email}
                onChange={(e) => setForm({ ...form, poc_email: e.target.value })}
                className={inputCls}
                placeholder="john.smith@navy.mil"
              />
            </Field>
            <Field label="POC phone">
              <input
                value={form.poc_phone}
                onChange={(e) => setForm({ ...form, poc_phone: e.target.value })}
                className={inputCls}
                placeholder="(760) 555-0101"
              />
            </Field>
            <Field label="CPARS rating">
              <input
                value={form.cpars_rating}
                onChange={(e) => setForm({ ...form, cpars_rating: e.target.value })}
                className={inputCls}
                placeholder="Exceptional"
              />
            </Field>
            <div className="md:col-span-2">
              <Field label="Outcome / results narrative">
                <textarea
                  value={form.outcome}
                  onChange={(e) => setForm({ ...form, outcome: e.target.value })}
                  rows={3}
                  className={inputCls}
                  placeholder="Delivered 12 days early with zero safety incidents. Received Exceptional CPARS in all six areas."
                />
              </Field>
            </div>
          </div>

          <div className="flex items-center justify-end gap-3 mt-6">
            <button
              onClick={cancel}
              className="text-xs font-mono font-bold tracking-widest text-slate-500 hover:text-slate-800 px-4 py-2.5"
              data-testid="pp-cancel-btn-2"
            >
              CANCEL
            </button>
            <button
              onClick={save}
              disabled={saving}
              className="bf-pill text-xs px-6 py-2.5 inline-flex items-center gap-2 disabled:opacity-50"
              data-testid="pp-save-btn"
            >
              {saving ? <Loader2 className="w-3.5 h-3.5 animate-spin" /> : <Save className="w-3.5 h-3.5" />}
              {saving ? "SAVING" : "SAVE"}
            </button>
          </div>
        </div>
      )}

      {/* List */}
      <div className="rounded-xl bg-white border border-slate-200 overflow-hidden">
        {loading ? (
          <div className="p-10 text-center text-slate-400 flex items-center justify-center gap-2">
            <Loader2 className="w-4 h-4 animate-spin" />
            Loading…
          </div>
        ) : items.length === 0 ? (
          <div className="p-10 text-center text-slate-500" data-testid="pp-empty-state">
            <AlertCircle className="w-8 h-8 text-slate-300 mx-auto mb-2" />
            {q
              ? `No past performance matches "${q}"`
              : "No past performance yet. Add your best projects so the AI can cite them in future proposals."}
          </div>
        ) : (
          <ul className="divide-y divide-slate-100" data-testid="pp-list">
            {items.map((pp) => (
              <li key={pp.pp_id} className="p-5 hover:bg-slate-50/60" data-testid={`pp-row-${pp.pp_id}`}>
                <div className="flex items-start gap-4">
                  <div className="w-10 h-10 rounded-xl bg-orange-100 text-orange-600 flex items-center justify-center shrink-0">
                    <Target className="w-5 h-5" />
                  </div>
                  <div className="flex-1 min-w-0">
                    <div className="flex items-start justify-between gap-3 flex-wrap">
                      <div>
                        <div className="font-display font-bold text-lg text-slate-900" data-testid={`pp-title-${pp.pp_id}`}>
                          {pp.project_title}
                        </div>
                        <div className="text-sm text-slate-600">
                          {pp.customer || "Customer n/a"}
                          {pp.contract_number ? ` · ${pp.contract_number}` : ""}
                        </div>
                      </div>
                      <div className="flex items-center gap-1.5 shrink-0">
                        <button
                          onClick={() => startEdit(pp)}
                          className="text-slate-400 hover:text-orange-600 p-1.5"
                          title="Edit"
                          data-testid={`pp-edit-${pp.pp_id}`}
                        >
                          <Pencil className="w-4 h-4" />
                        </button>
                        <button
                          onClick={() => remove(pp)}
                          className="text-slate-300 hover:text-rose-500 p-1.5"
                          title="Delete"
                          data-testid={`pp-delete-${pp.pp_id}`}
                        >
                          <Trash2 className="w-4 h-4" />
                        </button>
                      </div>
                    </div>

                    <div className="flex flex-wrap gap-x-4 gap-y-1 mt-2 text-xs font-mono text-slate-500">
                      <span>
                        VALUE <span className="text-emerald-700 font-bold">{money(pp.contract_value)}</span>
                      </span>
                      <span>
                        PERIOD{" "}
                        <span className="text-slate-800">
                          {(pp.period_start || "?") + " – " + (pp.period_end || "?")}
                        </span>
                      </span>
                      {pp.cpars_rating && (
                        <span className="inline-flex items-center gap-1">
                          <Star className="w-3 h-3 text-amber-500" />
                          <span className="text-amber-700 font-bold">{pp.cpars_rating}</span>
                        </span>
                      )}
                    </div>

                    {pp.scope && (
                      <p className="text-sm text-slate-700 mt-2 line-clamp-2">{pp.scope}</p>
                    )}

                    <div className="flex flex-wrap gap-1.5 mt-3">
                      {(pp.naics_codes || []).map((n) => (
                        <span
                          key={n}
                          className="text-[10px] font-mono font-bold tracking-widest bg-indigo-100 text-indigo-700 px-2 py-0.5 rounded-full"
                        >
                          NAICS {n}
                        </span>
                      ))}
                      {(pp.keywords || []).map((k) => (
                        <span
                          key={k}
                          className="text-[10px] font-mono font-bold tracking-widest bg-slate-100 text-slate-600 px-2 py-0.5 rounded-full"
                        >
                          {k}
                        </span>
                      ))}
                    </div>
                  </div>
                </div>
              </li>
            ))}
          </ul>
        )}
      </div>
    </div>
  );
}

const inputCls =
  "w-full bg-white border border-slate-200 rounded-lg px-3 py-2 text-sm focus:border-orange-400 outline-none";

function Field({ label, children, testid }) {
  return (
    <label className="block" data-testid={testid}>
      <div className="text-[10px] font-mono font-bold tracking-widest text-slate-500 mb-1">
        {label}
      </div>
      {children}
    </label>
  );
}
